import React from 'react'

import { Dashboard, PageLayout, SideSummary } from '@components/common'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  BackButton,
  Checkbox,
  Fieldset,
  HeadContent,
  Input,
  InputUI,
  Subsection,
} from '@fourthwall/components'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import { BadgePreview } from '../../components/BadgePreview'
import { getEmojiIcon } from '../../utils'
import { AddAnotherRule } from './components/AddAnotherRule'
import { BadgeType } from './components/BadgeType'
import { RulesSeparator } from './components/RulesSeparator'
import { SelectRule } from './components/SelectRule'
import { SelectedMembers } from './components/SelectedMembers'
import { SelectedTeamMembers } from './components/SelectedTeamMembers'
import { Summary } from './components/Summary'
import { UsernameStyling } from './components/UsernameStyling'
import { useBadgeCreateContext } from './hooks/useBadgeCreateContext'
import { FormFields } from './hooks/useBadgeForm/types'
import { BadgeCreateProvider } from './providers/BadgeCreateProvider/BadgeCreateProvider'

const BadgeCreateContent = () => {
  const {
    form,
    tiers,
    availableRules,
    hasEmptyRule,
    hasAnyRules,
    hasAvailableRules,
    usersQuery,
    isLoading,
    getRuleComponent,
  } = useBadgeCreateContext()

  return (
    <>
      <HeadContent title="Create a new badge" />
      <PageLayout
        leftContent={
          <>
            <Subsection>
              <BadgePreview
                text={form.values.text || 'Enter badge text'}
                emoji={getEmojiIcon(form.values.emoji)}
                backgroundColor={form.values.backgroundColor}
                borderColor={form.values.borderColor}
                textColor={form.values.textColor}
                iconOnly={form.values.badgeType === 'IconOnly'}
                withUsername
                usernameTextColor={
                  form.values.useCustomUsernameColor ? form.values.usernameTextColor : undefined
                }
              />
            </Subsection>
            <Subsection title="Badge styling">
              <Fieldset>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <Input label="Badge text" {...form.getFieldProps(FormFields.text)} />
                  </Fieldset.Item>
                </Fieldset.Row>
                <BadgeType form={form} />
              </Fieldset>
            </Subsection>
            <UsernameStyling form={form} />
            <Subsection title="Assign badge">
              <Fieldset>
                <Fieldset.Row>
                  <Checkbox
                    label="This badge can be earned by members"
                    {...form.getFieldProps(FormFields.canBeEarnedByMembers)}
                  />
                </Fieldset.Row>
                {form.values.canBeEarnedByMembers && (
                  <>
                    {!hasAnyRules && (
                      <Fieldset.Row inner>
                        <SelectRule form={form} options={availableRules} />
                      </Fieldset.Row>
                    )}
                    {form.values.rules.map((rule, index) => (
                      <React.Fragment key={index}>
                        <Fieldset.Row inner>{getRuleComponent(rule)}</Fieldset.Row>
                        {index === form.values.rules.length - 1 ? null : (
                          <Fieldset.Row inner>
                            <RulesSeparator />
                          </Fieldset.Row>
                        )}
                      </React.Fragment>
                    ))}
                    {hasAvailableRules && hasAnyRules && !hasEmptyRule && (
                      <AddAnotherRule form={form} />
                    )}
                    <Fieldset.Row inner>
                      <Checkbox
                        label="Remove badge when requirements are no longer met"
                        {...form.getFieldProps(FormFields.removeWhenRulesNoLongerMet)}
                      />
                    </Fieldset.Row>
                    {form.errors.rules && typeof form.errors.rules === 'string' && (
                      <Fieldset.Row inner>
                        <InputUI.Error $withoutMargin>{form.errors.rules}</InputUI.Error>
                      </Fieldset.Row>
                    )}
                  </>
                )}
                <Fieldset.Row>
                  <Checkbox
                    label="Give badge to selected members"
                    {...form.getFieldProps(FormFields.giveToSelectedMembers)}
                  />
                </Fieldset.Row>
                <SelectedMembers form={form} />
                <Fieldset.Row>
                  <Checkbox
                    label="Give badge to selected internal team staff"
                    {...form.getFieldProps(FormFields.giveToSelectedTeamStaff)}
                  />
                </Fieldset.Row>
                <SelectedTeamMembers form={form} usersQuery={usersQuery} />
                {form.errors.canBeEarnedByMembers && (
                  <Fieldset.Row>
                    <InputUI.Error $withoutMargin>{form.errors.canBeEarnedByMembers}</InputUI.Error>
                  </Fieldset.Row>
                )}
              </Fieldset>
            </Subsection>
          </>
        }
        rightContent={
          <SideSummary>
            <SideSummary.Item title="Rules for who gets badge">
              <Summary form={form} tiers={tiers} />
            </SideSummary.Item>
          </SideSummary>
        }
      >
        <Dashboard.Section
          name="Create a new badge"
          topContent={<BackButton label="Badges" to={routing.memberships.settings.badges.self} />}
          titleSpacing="medium"
          bottomMargin="large"
        />
      </PageLayout>
      <ConfirmBar
        isOpen={form.dirty}
        isLoading={isLoading}
        onConfirm={form.submitForm}
        onCancel={form.resetForm}
      />
      <LeavingGuard when={form.dirty} isValid={form.isValid} onSave={form.submitForm} />
    </>
  )
}

export const BadgeCreate = () => (
  <BadgeCreateProvider>
    <BadgeCreateContent />
  </BadgeCreateProvider>
)
